import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import {
  ALL_VALUE,
  CenterSpinner,
  DateFilterField,
  EmptyText,
  FilterField,
  FilterPanel,
  ListHeaderRow,
  LoadMore,
  M,
  RowKV,
  SelectSheet,
  StatCard,
  StatusChip,
  SummaryCard,
  useSheet,
} from "../../../components/managementUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { formatMoney, formatMoneyToString } from "../../../utils/financeFormat";
import { useEmployeeAuth } from "../../../auth/EmployeeAuthContext";
import { JOB_TITLE, filterJobPosition, useSupportBusinessEndpoint } from "../../../utils/managerRole";
import {
  ALL_OPTION,
  buildEmployeeSheetData,
  buildOptionSheetData,
  useFilterOptions,
  validId,
} from "../../../utils/useFilterOptions";
import {
  SELL_SORT_OPTIONS,
  SELL_TYPES,
  TRANSACTION_STATUS_OPTIONS,
  getByPath,
  transactionStatusUi,
} from "./sellConfig";
import { EMP_BASE_PATH } from "../../../config";

const LIMIT = 10;
const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const TxCard = ({ item, cfg, onDetail }) => {
  const ui = transactionStatusUi(item?.status);
  const projectName = item?.project?.name || "";
  const title = `${projectName} ${projectName ? "-" : ""} ${cfg.apartmentCode(item)}`.trim();

  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      onClick={onDetail}
      bg="white"
      border="1px solid"
      borderColor={M.border}
      borderRadius="12px"
      p="14px"
    >
      <Box minW="0" maxW="72%">
        <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
          {title || "-"}
        </Text>
        <Box h="8px" />
        <Flex align="center" gap="8px" flexWrap="wrap">
          <Text fontSize="14px" fontWeight={500} color={M.baseContent}>
            {fmtDate(item?.[cfg.dateField])}
          </Text>
          <StatusChip label={ui.label} bg={ui.bg} color={ui.color} />
        </Flex>
      </Box>

      <Box h="1px" bg={M.border} mt="12px" mb="12px" />

      <Flex direction="column" gap="8px">
        {cfg.rows.map((r) => {
          const raw = getByPath(item, r.path);
          let value = raw;
          if (r.format === "money") value = formatMoney(raw || 0);
          if (r.format === "date") value = fmtDate(raw);
          return (
            <RowKV
              key={r.label}
              label={r.label}
              value={value || "-"}
              valueColor={r.highlight ? M.primary : undefined}
            />
          );
        })}
      </Flex>
    </Box>
  );
};

const SellTransactionList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { type } = useParams();
  const cfg = SELL_TYPES[type];
  const { user } = useEmployeeAuth();
  const jobPosition = filterJobPosition(user);
  const employeeSheetKey = useSupportBusinessEndpoint(user) ? "EMPLOYEE_SUPPORT" : "EMPLOYEE";

  const [filterOpen, setFilterOpen] = useState(true);
  const [status, setStatus] = useState(ALL_VALUE);
  const [project, setProject] = useState(ALL_OPTION);
  const [agent, setAgent] = useState(ALL_OPTION);
  const [employee, setEmployee] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [area, setArea] = useState(ALL_OPTION);
  const [branch, setBranch] = useState(ALL_OPTION);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stat, setStat] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(() => {
    if (sheet.type === "BLOCK") return { saleAreaId: validId(area) };
    if (sheet.type === "EMPLOYEE" || sheet.type === "EMPLOYEE_SUPPORT")
      return { saleBlockId: validId(block), saleAreaId: validId(area) };
    return undefined;
  }, [sheet.type, area, block]);
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const sortOption = SELL_SORT_OPTIONS.find((s) => s.key === sortKey) || SELL_SORT_OPTIONS[0];

  const buildParams = useCallback(
    (p) => ({
      page: p,
      limit: LIMIT,
      startTime: fromDate ? moment(fromDate).format("YYYY-MM-DD") : undefined,
      endTime: toDate ? moment(toDate).format("YYYY-MM-DD") : undefined,
      status: status !== ALL_VALUE ? status : undefined,
      sortBy: sortOption.sortBy,
      sortOrder: sortOption.sortOrder,
      saleAreaId: validId(area),
      saleBlockId: validId(block),
      branchId: validId(branch),
      projectId: validId(project),
      agentId: validId(agent),
      bookerId: validId(employee),
    }),
    [fromDate, toDate, status, sortOption, area, block, branch, project, agent, employee]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      if (!cfg) return;
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const params = buildParams(p);
        const [data, statistic] = await Promise.all([
          cfg.listApi(params),
          p === 1 && cfg.statisticApi ? cfg.statisticApi(params).catch(() => null) : Promise.resolve(null),
        ]);
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setTotal(data?.pagination?.total ?? list.length);
        if (statistic) setStat(statistic);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [cfg, buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    status,
    fromDate,
    toDate,
    sortKey,
    area?._id,
    block?._id,
    branch?._id,
    project?._id,
    agent?._id,
    employee?._id,
  ]);

  const statusLabel = TRANSACTION_STATUS_OPTIONS.find((s) => s.key === status)?.label ?? "Tất cả";

  // Lưới bộ lọc theo chức vụ - khớp renderFilterGrid của app.
  const filterFields = useMemo(() => {
    const f = {
      status: (
        <FilterField
          key="status"
          label="Trạng thái"
          value={statusLabel}
          onClick={() => sheet.open("STATUS")}
          width={
            jobPosition === JOB_TITLE.NHAN_VIEN ||
            jobPosition === JOB_TITLE.TRUONG_KHOI ||
            jobPosition === JOB_TITLE.TRUONG_VUNG
              ? "100%"
              : "48%"
          }
        />
      ),
      project: (
        <FilterField key="project" label="Dự án" value={project?.name || "Tất cả"} onClick={() => sheet.open("PROJECT")} />
      ),
      agent: <FilterField key="agent" label="Đại lý" value={agent?.name || "Tất cả"} onClick={() => sheet.open("AGENT")} />,
      employee: (
        <FilterField
          key="employee"
          label="Nhân viên"
          value={employee?._id !== "all" ? employee?.fullName || "Tất cả" : "Tất cả"}
          onClick={() => sheet.open(employeeSheetKey)}
        />
      ),
      block: <FilterField key="block" label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />,
      area: <FilterField key="area" label="Vùng" value={area?.name || "Tất cả"} onClick={() => sheet.open("AREA")} />,
      branch: (
        <FilterField key="branch" label="Chi nhánh" value={branch?.name || "Tất cả"} onClick={() => sheet.open("BRANCH")} />
      ),
      from: <DateFilterField key="from" label="Từ ngày" value={fromDate} onChange={setFromDate} />,
      to: <DateFilterField key="to" label="Đến ngày" value={toDate} onChange={setToDate} />,
    };
    switch (jobPosition) {
      case JOB_TITLE.NHAN_VIEN:
        return [f.status, f.from, f.to];
      case JOB_TITLE.TRUONG_KHOI:
      case JOB_TITLE.TRUONG_VUNG:
        return [f.status, f.project, f.agent, f.employee, f.block, f.from, f.to];
      case JOB_TITLE.TRUONG_PHONG:
        return [f.status, f.project, f.agent, f.employee, f.from, f.to];
      default:
        return [f.status, f.project, f.agent, f.employee, f.branch, f.area, f.block, f.from, f.to];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition, statusLabel, project, agent, employee, block, area, branch, fromDate, toDate, employeeSheetKey]);

  const sheetTitle =
    {
      STATUS: "Trạng thái",
      SORT: "Sắp xếp",
      PROJECT: "Dự án",
      AGENT: "Đại lý",
      EMPLOYEE: "Nhân viên",
      EMPLOYEE_SUPPORT: "Nhân viên",
      BLOCK: "Khối",
      AREA: "Vùng",
      BRANCH: "Chi nhánh",
    }[sheet.type] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "STATUS") return TRANSACTION_STATUS_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT") return SELL_SORT_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "PROJECT") return buildOptionSheetData(options.items, project);
    if (sheet.type === "AGENT") return buildOptionSheetData(options.items, agent);
    if (sheet.type === "EMPLOYEE" || sheet.type === "EMPLOYEE_SUPPORT")
      return buildEmployeeSheetData(options.items, employee);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    if (sheet.type === "BRANCH") return buildOptionSheetData(options.items, branch);
    return [];
  }, [sheet.type, options.items, project, agent, employee, block, area, branch]);

  const selectedKey = {
    STATUS: status,
    SORT: sortKey,
    PROJECT: project?._id,
    AGENT: agent?._id,
    EMPLOYEE: employee?._id,
    EMPLOYEE_SUPPORT: employee?._id,
    BLOCK: block?._id,
    AREA: area?._id,
    BRANCH: branch?._id,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "STATUS") setStatus(raw);
    if (sheet.type === "SORT") setSortKey(raw);
    if (sheet.type === "PROJECT") setProject(raw);
    if (sheet.type === "AGENT") setAgent(raw);
    if (sheet.type === "EMPLOYEE" || sheet.type === "EMPLOYEE_SUPPORT") setEmployee(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "AREA") setArea(raw);
    if (sheet.type === "BRANCH") setBranch(raw);
    sheet.close();
    setSheetSearch("");
  };

  if (!cfg) {
    return (
      <Box maxW="480px" mx="auto">
        <AppBar title="Quản lý bán hàng" />
        <EmptyText text="Không tìm thấy loại giao dịch" />
      </Box>
    );
  }

  // Thẻ tổng: loại "money" lấy tổng danh số (giá tính hoa hồng) từ API thống kê.
  const statValue = (key) => (cfg.statKeys ? stat?.[cfg.statKeys[key]] : undefined);
  const summaryTotal = cfg.summary.kind === "money" ? formatMoney(statValue("commission") || 0) : total;

  const selectSheet = (
    <SelectSheet
      isOpen={sheet.isOpen}
      onClose={() => {
        sheet.close();
        setSheetSearch("");
      }}
      title={sheetTitle}
      data={sheetData}
      selectedKey={selectedKey}
      onSelect={onPick}
      searchable={!["STATUS", "SORT"].includes(sheet.type)}
      searchValue={sheetSearch}
      onChangeSearch={setSheetSearch}
      loading={options.loading}
      loadingMore={options.loadingMore}
      hasNextPage={options.hasMore}
      onEndReached={options.loadMore}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  // Cột của bảng dựng từ chính cfg.rows đang dùng cho card mobile.
  if (isDesktop) {
    const rows = items.map((it) => {
      const projectName = it?.project?.name || "";
      const cells = {};
      cfg.rows.forEach((r) => {
        const raw = getByPath(it, r.path);
        let value = raw;
        if (r.format === "money") value = formatMoney(raw || 0);
        if (r.format === "date") value = fmtDate(raw);
        cells[r.label] = value || "-";
      });
      return {
        id: it._id,
        raw: it,
        ui: transactionStatusUi(it?.status),
        title: `${projectName} ${projectName ? "-" : ""} ${cfg.apartmentCode(it)}`.trim() || "-",
        date: fmtDate(it?.[cfg.dateField]),
        ...cells,
      };
    });

    const statItems = [
      { key: "summary", title: cfg.summary.text, total: summaryTotal },
      ...(cfg.statCards || []).map((c) => ({
        key: c.subTitle,
        title: c.title,
        total:
          c.format === "short"
            ? formatMoneyToString(statValue(c.from) || 0)
            : statValue(c.from) ?? total,
      })),
    ];

    return (
      <DesktopPage title={cfg.title}>
        <DesktopStatRow items={statItems} />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            {filterFields.map((el, i) =>
              React.cloneElement(el, { key: el.key || i, width: "200px" }),
            )}
            <Box w="200px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có giao dịch"
            onRowClick={(row) =>
              navigate(`${EMP_BASE_PATH}/quan-ly/ban-hang/${type}/${row.id}`)
            }
            rows={rows}
            headers={[
              { key: "title", label: "Dự án / Căn hộ", align: "left" },
              { key: "date", label: "Ngày" },
              ...cfg.rows.map((r) => ({
                key: r.label,
                label: r.label,
                align: r.format === "money" ? "right" : "left",
                render: r.highlight
                  ? (row) => (
                      <Text fontSize="14px" fontWeight={600} color={M.primary}>
                        {row[r.label]}
                      </Text>
                    )
                  : undefined,
              })),
              {
                key: "status",
                label: "Trạng thái",
                render: (row) => (
                  <StatusChip label={row.ui.label} bg={row.ui.bg} color={row.ui.color} />
                ),
              },
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {selectSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title={cfg.title} />

      <Box px="16px" pt="12px">
        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          {filterFields}
        </FilterPanel>

        <Box h="12px" />
        <SummaryCard text={cfg.summary.text} total={summaryTotal} />

        {cfg.statCards ? (
          <>
            <Box h="12px" />
            <Flex gap="12px">
              {cfg.statCards.map((c) => (
                <StatCard
                  key={c.subTitle}
                  subTitle={c.subTitle}
                  title={c.title}
                  value={
                    c.format === "short"
                      ? formatMoneyToString(statValue(c.from) || 0)
                      : statValue(c.from) ?? total
                  }
                  icon={c.icon}
                  bgColor={c.bgColor}
                  textColor={c.textColor}
                  width="calc(50% - 6px)"
                />
              ))}
            </Flex>
          </>
        ) : null}

        <ListHeaderRow title={cfg.listTitle} sortLabel={sortOption.label} onSort={() => sheet.open("SORT")} />

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText text="Không có giao dịch" />
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it) => (
              <TxCard
                key={it._id}
                item={it}
                cfg={cfg}
                onDetail={() => navigate(`${EMP_BASE_PATH}/quan-ly/ban-hang/${type}/${it._id}`)}
              />
            ))}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>

      {selectSheet}
    </Box>
  );
};

export default SellTransactionList;
